/**
 * Authentication Service for Broker Portal
 * Acts as a facade between components and the store/repository
 */

import store from '@/store'
import authRepository from '../repositories/auth.repository'

interface LoginResponse {
  success: boolean
  error?: string
}

export interface UserAccount {
  id: number
  login: string
  firstName: string
  lastName: string
  email: string
  imageUrl: string
  activated: boolean
  langKey: string
  authorities: string[]
}

class AuthService {
  /**
   * Login user with credentials
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    const result = await store.dispatch('broker/login', { username, password })
    return result
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    await store.dispatch('broker/logout')
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return store.getters['broker/isAuthenticated'] || authRepository.isAuthenticated()
  }

  /**
   * Get current user
   */
  getCurrentUser(): any {
    return store.getters['broker/currentUser']
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return store.getters['broker/authToken'] || authRepository.getToken()
  }

  /**
   * Get partner ID
   */
  getPartnerId(): number | null {
    return store.getters['broker/partnerId'] || authRepository.getPartnerId()
  }

  /**
   * Check and restore auth state from localStorage
   */
  async checkAuthState(): Promise<boolean> {
    return await store.dispatch('broker/checkAuth')
  }

  /**
   * Get authorization headers for API calls
   */
  getAuthHeaders(): { Authorization: string } | {} {
    return authRepository.getAuthHeader()
  }

  /**
   * Get current user account information including profile picture
   */
  async getUserAccount(): Promise<UserAccount> {
    return await authRepository.getUser()
  }
}

export default new AuthService()